/**
 * **节点包（Node Package）** —— 内核与领域能力的边界。
 *
 * ## 为什么要有这一层
 *
 * 目标是让这个编辑器从「打印专用」变成「通用」：内核只留**机制**
 * （图、行、端口、控件、求值调度、编辑操作），所有**具体**的节点
 * （数据源、文本、条形码、日期…）由**包**提供，可选加载。
 *
 * 判定标准一句话：**把「打印」两个字从内核删掉，内核还应该完整可用。**
 *
 * ## 内核留什么 / 包拿什么
 *
 * | | 内核 | 包 |
 * |---|---|---|
 * | 图 | 节点 / 连线 / 选中 / 布局 / 历史 / 剪贴板 | — |
 * | 行与控件 | 行类型（display/property/widget/group/repeater/input/output）+ 通用控件 | — |
 * | 求值 | **调度**：拓扑序、缓存、循环检测、诊断收集 | **实现**：每种节点怎么算 |
 * | 编辑操作 | 对齐 / 分布 / 框 / 注释 / 接通节点 | — |
 * | 目录 | 「包能注册节点」这个机制 | 具体有哪些节点、叫什么、什么分类 |
 *
 * ⚠️ **求值器的拆分是下一阶段**（见 `docs/node-packages.md` §3）：
 * 现在 `evaluateGraph` 仍然是「一个调度函数 + 按类型分支」，
 * 包还没有自己的求值器。这一层先把**目录**搬出去，
 * 让「包」这个概念在代码里立住；求值器的可插拔是独立的下一步。
 *
 * ## 分阶段推进（每一步都能独立验证）
 *
 *   1. ✅ 本文件：包/注册表的形状 + 领域节点全部搬出内核
 *      （原来的印刷类节点**整个删掉了** —— 不是搬进 `domains/print`：
 *       领域内容属于包，而这个仓库里已经没有任何印刷领域包）
 *   2. ✅ 求值调度与实现拆开，包提供 `evaluators`（代码包）
 *   3. ✅ 包清单 + 可选加载 + 缺失占位（**绝不能因此拒绝打开文档**）
 *   4. ✅ 组节点资产（用户搭好的子图，当单个节点用）
 *   5. ✅ 编辑器贡献：包注册一种新编辑器（+ 它自己的侧栏面板）。
 *      形状在 `types/editor.ts`，合并与查重在 `layout/editorRegistry.ts`，
 *      装配点在 `playground/nodeTypes.ts`，端到端验证的夹具包在
 *      `scripts/fixtures/editor-package.ts`（**主程序里没有内置的 UI 包**）
 */
import { assetProblems as assetProblemsOf, type GroupAssetDef } from './asset'
import { GROUP_INPUT_NODE, GROUP_OUTPUT_NODE } from './group'
import { findSocketIn } from './sockets'
import { canConnect } from '../types/socket'
import { brokenEvaluator, compileEvaluatorSource, type EvaluatorSource } from './evaluatorSource'
import { cloneRows } from './rows'
import { CATEGORY_ICONS } from './nodeIcons'
import type { NodeEvaluator } from './evaluate'
import type { NodeRunner } from './runGraph'
import type { HostCapability, TriggerKind } from '../types/capability'
import type { GraphNodeModel, NodeRow, SubGraph } from '../types/node'
import type { EditorContribution } from '../types/editor'

/**
 * 一个**分类**。
 *
 * ⚠️ 从「内核里的联合类型」变成「包提供的数据」是有意的：
 * 分类的中文名是包的文案，不该写死在内核里。
 * 而分类的**颜色**仍然留在主题层（`theme/tokens.ts`）——
 * 颜色是呈现，不是能力；未知分类在那边有兜底色。
 */
export interface NodeCategoryDef {
  id: string
  label: string
  /**
   * ★ **这一类的图标**（24×24 stroke 路径）—— 它是**回退**：
   * 节点类型自己声明了 `icon` 就用它，没声明就用这个。
   *
   * 为什么要有这一层：一个包动辄几十个节点类型（内置的就有 78 个），
   * 逐个画图标既不现实、也没多少信息量 —— "文本 · 转大写"和"文本 · 转小写"
   * 的图标没法一眼分开，那是**标题**的活。图标要回答的是**更大的一格**：
   * 这是个常量 / 文本运算 / 打印组件 / 事件……
   */
  icon?: string
}

/**
 * **节点头部上的一个动作按钮**（用户 2027 要的"抽象到节点层次"）。
 *
 * 用户原话：
 *
 * > 然后是 js 代码节点，可以给增加一个按钮（静音按钮旁，跳转打开属性面板），
 * > **需要所有节点都支持在节点 header 添加按钮**，而不是只为 js 代码节点
 * > 添加这个功能，需要抽象到节点层次
 *
 * ## 为什么是"声明 id"而不是"带一个函数"
 *
 * `NodeTypeDef` 是**数据** —— 数据包（`node-print.package` 那种 JSON）也能
 * 贡献节点类型，函数没法序列化。而且"点了之后干什么"是**宿主**的事
 * （打开属性面板 = 切窗口 + 设活动节点，内核不认识窗口）。
 *
 * 于是分工是：
 * ```text
 *   包 / 类型声明   ：这里要有个按钮（id + 图标 + 一句话）
 *   宿主            ：这个 id 干什么是我的事（见 playground 的 NODE_ACTIONS）
 * ```
 *
 * ⚠️ 宿主**不认识**的 id 会在注册时就被指出来（`verify:packages` 盯着），
 * 不给"点了没反应"留活口。
 */
export interface NodeActionDef {
  /**
   * 动作标识。
   *
   * 内核只约定几个通用 id（见 `NODE_ACTIONS`）：`settings`（打开这个节点的
   * 属性面板）…域包要加自己的动作时，得由装了它的宿主提供实现。
   */
  id: string
  /**
   * 按钮里的图标：**24×24 viewBox 的 stroke 路径**（和 `EditorMeta.icon`
   * 同一套画法）。
   */
  icon: string
  /** 悬停说明（一句话，动词开头） */
  label: string
}

export interface NodeTypeDef {
  /** 节点类型标识，对应求值器注册名 */
  type: string
  title: string
  category: string
  /**
   * ★ **节点图标**：24×24 viewBox 的 stroke 路径（和 `EditorMeta.icon`
   * 同一套画法）。
   *
   * 用户原话：
   *
   * > 关于节点类型，是不是可以给节点配置一些图标，不然**改了名字不能一眼
   * > 看懂这是什么节点**，加了图标就可以不用给节点设置默认名字添加类型了
   *
   * 所以它解决的是"标题被改过之后认不出来"这件事 —— 图标是**类型**的
   * 信号，名字是**用户**的信号，两者互不覆盖。
   *
   * ⚠️ 它**不进文档**（不是 `DESIGN_KEYS` 里那种模型字段）：图标由宿主
   * 按类型现查（和 `hint` 一样）—— 包升级换了图标，老文档跟着变才是对的；
   * 而且它不改变任何输出，也不该占文档的键。
   * 没给图标的类型回落到一个「!」（悬停仍然有名称 / 类型 / 作用）。
   */
  icon?: string
  /**
   * ★ **节点头部上的动作按钮**（可选，按类型声明）。
   *
   * 例：JS 代码节点的代码在属性面板里，节点上放一个"打开属性面板"的按钮
   * 比让人去找面板强得多。机制是通用的 —— 任何类型都能声明。
   */
  actions?: readonly NodeActionDef[]
  /** 新建时的默认宽度 */
  width: number
  /** 新建时的默认高度（只有框需要） */
  height?: number
  /**
   * 搜索关键词。
   *
   * 标题不够用：「组件 · 条形码」得能被 `barcode` / `条码` / `zpl` 搜到。
   */
  keywords: string[]
  /**
   * 一句话说明「这个节点是干什么的」。
   *
   * 显示在标题左边那个感叹号图标的悬停提示里 ——
   * 光看类型名（`component.text`）新人根本不知道它是干嘛的。
   */
  hint?: string
  /**
   * **这个节点是一种触发器**：它能让图跑起来（见 `types/capability.ts`）。
   *
   * ⚠️ 声明在**节点类型**上而不是包上：一份文档用了哪些触发方式，
   * 由它里面**摆了哪些节点**决定 —— 包声明是"这个包可能带什么"，
   * 粒度太粗（装了事件包不等于这份文档就有定时器）。
   *
   * 有了它，`requirementOf(文档里的节点类型)` 就能回答
   * "这份文档需要什么宿主才能跑"，于是"缺能力 → 拒绝执行并说清缺什么"
   * 才有依据（不是运行时才发现跑不动）。
   */
  trigger?: TriggerKind
  /**
   * **这个节点需要宿主提供什么能力**（网络 / 文件 / 设备…）。
   *
   * 同 `trigger`：声明在节点类型上，按文档实际用到的节点汇总。
   */
  capabilities?: readonly HostCapability[]
  /**
   * 渲染形态。见 types/node.ts 的 GraphNodeModel.render：
   *   'dot'   小圆点（接通节点）
   *   'frame' 可拖动的矩形（框节点）
   */
  render?: 'default' | 'dot' | 'frame' | 'note'
  /** 编辑器专用：不参与求值，也不报「未注册求值器」 */
  editorOnly?: boolean
  /**
   * 不出现在「添加节点」菜单里。
   *
   * 接通节点是**对线操作**的产物（双击线条创建），不是让人从菜单里挑的 ——
   * 放进去只会多一个没人从菜单里选的项。
   */
  hidden?: boolean
  /**
   * 行**模板** —— 数据，不是工厂函数。
   *
   * ⚠️ 这里原来是 `build: () => NodeRow[]`（工厂函数）。改成数据有两个原因：
   *
   *   ① **数据包需要它**。一个用 JSON 描述的节点（可选加载的领域包）
   *      没法携带函数 —— 行必须是可序列化的形态，代码路径和数据路径
   *      才能共用同一套形状；
   *   ② **工厂函数是个人为的坑**。「你记得写工厂了吗」这件事靠自觉，
   *      忘了就是「改一个节点改掉所有同类型节点」（真踩过）。
   *      现在由 `makeModelOfType` 统一深拷贝，写的人不需要记得。
   *
   * ⚠️ 模板本身是共享的（那没问题），但**实例是拷贝出来的** ——
   * 别在别处拿到 `def.rows` 直接改。
   */
  rows: NodeRow[]
  /**
   * **数据包**的求值逻辑：源码文本（见 `evaluatorSource.ts`）。
   *
   * ⚠️ 和包级 `evaluators[type]` **二选一**：
   *   · 代码包（自家 TS）写成函数，放 `NodePackage.evaluators`；
   *   · 数据包（JSON，可选加载）带源码文本，放这里；
   *   · 两边都给同一个类型 → 注册表**抛错**（那是复制粘贴出来的重复）。
   */
  code?: EvaluatorSource
}

/**
 * 一个节点包。
 *
 * ⚠️ **包之间不许互相 import 具体节点**。需要别人的节点就在图里用，
 * 而不是在自己的定义里引用 —— 否则「可选加载」立刻变成
 * 「必须一起装」。包与包之间只允许声明依赖（`requires`，见 §5）。
 */
export interface NodePackage {
  /** 稳定标识，会写进文档里（`print` / `ui` …） */
  id: string
  /** 显示名（加载/缺失提示里用） */
  label: string
  /**
   * **三档**，而且每一档都有实际后果（不是分类学）：
   *
   * | kind | 是什么 | 能不能卸载 |
   * |---|---|---|
   * | `core` | 内核自己的节点（常量族 / 结构构造） | ❌ 强制加载 |
   * | `base` | **核心基础能力**：基础类型、基础节点、各种函数节点（求值类） | ❌ **永远启用** |
   * | `domain` | 领域能力（数据源、打印域…） | ✅ 可选加载 / 卸载 |
   *
   * ⚠️ `base` 这一档是用户 2027 定的：
   * 「这种内置包**不算在『节点包能不能启动』**里，默认都是启动的，
   * 算核心基础功能，求值类的。」
   * 也就是说：**函数节点不是"可选能力"，是编辑器本来就该有的东西** ——
   * 把 `logic.if` / `text.upper` 做成能卸载的包，等于让用户能把编辑器的
   * 基本功卸掉，而卸掉之后画布上那些节点只会算不出值。
   *
   * ⚠️ 于是 `disabled` 名单**只对 `domain` 生效**（`setPackages` 里那条过滤），
   * 而「节点包」菜单里 `base` / `core` 是**只读的一行**（没有卸载入口）。
   *
   * ⚠️ `verify:boundary` 那条「内核不许引用它的节点类型」查的是
   * **`domain` 包**：`base` 的类型本来就跟内核一起发（而且内核在
   * `group` / `loop` / `frame` 这些构造上分支是应该的）—— 全查会满屏误报。
   * `verify:layers` 那条编辑器标识的守卫同理，只看 `domain`。
   */
  kind: 'core' | 'base' | 'domain'
  /** 这个包贡献的分类 —— 顺序就是「添加节点」菜单里的分组顺序 */
  categories: NodeCategoryDef[]
  /** 这个包贡献的节点类型 */
  nodeTypes: NodeTypeDef[]
  /**
   * 这个包贡献的求值器：节点类型标识 → 实现。
   *
   * ⚠️ 求值器**跟着节点定义走**：定义了 `source` 就得能算 `source`。
   * 分成两个包两份清单，只会出现「节点加了、求值器忘了加」
   * （表现是运行时一条「未注册求值器」的诊断）。
   */
  evaluators?: Record<string, NodeEvaluator>
  /**
   * 这个包贡献的**副作用执行器**（运行期用，见 `node/runGraph.ts`）。
   *
   * ⚠️ 和 `evaluators` 是**两件事**，不要合并：
   *   · `evaluators` 是**纯函数**，设计期就会跑（每改一次文档跑一次）；
   *   · `runners` 是**副作用**，只在用户点"运行"（或事件触发）时跑一次。
   * 合并的话，设计期每敲一个字符就可能发一次网络请求 —— 那是灾难。
   *
   * ⚠️ 哪些节点该有 runner、哪些该有 evaluator，由节点语义决定；
   * 一个节点**可以两个都有**（设计期给占位、运行期给真值），
   * 但绝不能只有 runner 而没有 evaluator（否则设计期会报"未注册求值器"）。
   */
  runners?: Record<string, NodeRunner>
  /**
   * 这个包带的**组节点资产**（搭好的子图，当单个节点用）。
   *
   * ⚠️ 资产是**纯数据**（一张图），所以数据包天然能带它 ——
   * 它比 `evaluators` 更适合外部包（函数放不进 JSON，图可以）。
   */
  assets?: GroupAssetDef[]
  /**
   * 这个包贡献的**编辑器类型**（见 `types/editor.ts` 的 `EditorContribution`）。
   *
   * 例子：打印包带一个「页面编辑器」—— 它必须是**独立的 Area 编辑器类型**，
   * 而不是把通用编辑器改成打印编辑器的形状（见 `docs/package-boundaries.md` §0）。
   *
   * ⚠️ 和 `evaluators` 同一条界线：**只有代码包能给**。`component` 是活的东西，
   * JSON 里放不下 —— 数据包（用户导入的 `.np-package.json`）写了 `editors`
   * 会被 `parsePackageFile` **明确拒绝**，而不是静默丢掉那个字段
   * （静默丢掉的表现是「我明明写了编辑器，为什么菜单里没有」）。
   *
   * ⚠️ 注册表那半边（合并 / 查重 / 序号 / 内核冲突）在
   * `layout/editorRegistry.ts` —— 编辑器是 UI，内核目录在那儿。
   * 这里只是「包能带这个东西」的形状声明。
   */
  editors?: readonly EditorContribution[]
  /**
   * ★ **这个包带的示例文档**（2027 加，用户原话）：
   *
   * > 再给我一个示例文档（娱乐，域节点包可以带示例文档，被自动加载到
   * > 文件菜单下的示例文件菜单下）
   *
   * 于是「这个包能干什么」有了一个**能打开的答案**：不用先看文档、
   * 也不用自己搭图 —— 文件 → 示例文件 → 点一下它就在编辑器里了。
   *
   * ⚠️ 形状和 `assets` **刻意保持一致**（图就是一张 `SubGraph`：节点是
   * **模型**，不是序列化之后的文档）。两个理由：
   *   · 数据包（JSON）放不下模型里的函数与组件 → 示例**只有代码包能给**，
   *     和 `editors` / `evaluators` 同一条界线；
   *   · 用模型而不是 `GraphDocument`，这一层就不必 import `graph/`
   *     （`node/` 只许依赖 `types/`，`verify:layers` 守着）。
   *     序列化成文档是**宿主**在打开那一刻做的事。
   */
  examples?: readonly PackageExample[]
}

/**
 * 一份**示例文档**（包带来的"打开就能看"的东西）。
 *
 * ⚠️ `uuid` 是**固定**的，不是每次生成 —— 因为示例文档也是文档：
 * 反复打开同一个示例时库里的 UUID 会撞上，于是那个"要不要覆盖"的弹窗
 * 自然就生效了（用户定的那条规则在这里第一次有了真实用途：
 * 示例更新了 → 覆盖；自己改过 → 另存为）。
 * 前缀用 `example-`，一眼认得出它不是用户自己建的文档。
 */
export interface PackageExample {
  /** 稳定标识，形如 `print.example.fortune` */
  id: string
  /** 菜单里显示的名字（也是收进文档库之后的文档名） */
  name: string
  /** 一句话说明「这个示例演示什么」（菜单里的副标题） */
  hint?: string
  /** 文档 UUID（固定；见上面的说明） */
  uuid: string
  /** 图 —— **每次都新建一份**（模型是可变的，共享一份会被改坏） */
  graph: () => SubGraph
}

/**
 * **这份文档需要什么宿主** —— 按它实际用到的节点类型汇总。
 *
 * ⚠️ 粒度选在**节点类型**上（不是包上）：装了事件包 ≠ 这份文档有定时器。
 * 所以「需要什么能力」必须从"文档里摆了哪些节点"推出来，
 * 否则工具卡片会写一堆这份文档根本用不到的声明。
 */
export function requirementOf(
  nodeTypes: readonly NodeTypeDef[],
): { capabilities: HostCapability[]; triggers: TriggerKind[] } {
  const capabilities = new Set<HostCapability>()
  const triggers = new Set<TriggerKind>()
  for (const def of nodeTypes) {
    for (const capability of def.capabilities ?? []) capabilities.add(capability)
    if (def.trigger) triggers.add(def.trigger)
  }
  return { capabilities: [...capabilities], triggers: [...triggers] }
}

export interface NodeRegistry {
  /** 全部包合起来之后的节点目录（顺序 = 包顺序 × 包内顺序） */
  nodeTypes: NodeTypeDef[]
  /** 全部包合起来之后的分类 */
  categories: NodeCategoryDef[]
  /** 全部包合起来之后的求值器 */
  evaluators: Record<string, NodeEvaluator>
  /** 全部包合起来之后的**副作用执行器**（只有代码包能给） */
  runners: Record<string, NodeRunner>
  /**
   * 源码编译不过的节点类型 —— 给「包校验」界面用的。
   *
   * ⚠️ 编译失败**不阻止注册**：节点照常出现在菜单里、照常能摆到画布上，
   * 只是求值时在它自己身上报一条具体诊断。理由是「半可用的包」
   * 比「整个包消失」好排查得多 —— 后者只会让人以为包没装上。
   */
  codeProblems: { type: string; problems: { message: string; at: number }[] }[]
  /** 全部包合起来的资产（菜单里的「添加资产」用它） */
  assets: GroupAssetDef[]
  /** 全部包合起来的**示例文档**（「文件 → 示例文件」按这个清单生成菜单） */
  examples: PackageExample[]
  /**
   * 有问题的资产 —— 结构不对，或者**内部图引用了不存在的节点类型**。
   *
   * ⚠️ 这一条是踩出来的：一个资产里面用了 `text.pad`，而那个节点
   * **当时还没写**。表现是放出来算不出值、节点上一条「未注册求值器」，
   * 而**资产本身**看起来完全正常 —— 得靠「求值诊断指向组内某个 id」
   * 才能反推。校验期就能查出来的事，别留给运行时。
   *
   * ⚠️ 查的是**合并之后**的目录：一个包里的资产用另一个包的节点是合法的
   * （日期资产用数据源节点很自然）。所以只有「整个注册表里都没有」才算问题。
   */
  assetProblems: { asset: string; problems: string[] }[]
  nodeTypeOf: (type: string) => NodeTypeDef | undefined
  /**
   * **这个节点类型是哪个包给的**（`NodePackage.id`）。
   *
   * ⚠️ 它存在的理由只有一个：让**文档**能记下"这个节点从哪来"
   * （`serialization.ts` 的 `pkg`），于是"这份模板依赖哪些包"不必先有
   * 注册表才能回答。文档里**不抄**包内清单（那会漂），只记这一个来源。
   */
  packageOf: (type: string) => string | undefined
  categoryLabel: (id: string) => string
  /** 按类型产出模型；类型没登记返回 null（调用方据此提示） */
  makeModelOfType: (type: string, id: string) => GraphNodeModel | null
}

/**
 * 把若干包合成一个注册表。
 *
 * ⚠️ **重复的类型标识要抛错，而不是「后者覆盖前者」**。
 * 两个包装了同名的节点，是一个必须当场知道的错误：
 * 静默覆盖的表现是「加载顺序变了，某个节点的行为就变了」，
 * 那种 bug 没有任何现场。
 */
export interface RegistryOptions {
  /**
   * 菜单里的**分组显示顺序**（分类 id 列表）。
   *
   * ⚠️ 显示顺序和包顺序是**两件事**，故意分开：
   *   · 包顺序 = 「注册表里节点的顺序」，内核包排前面很自然；
   *   · 分组顺序 = **呈现决策**：用户先找「数据源」还是先找「框」。
   * 混在一起的话，为了调菜单顺序就得去改包的顺序，而包的顺序
   * 又会影响节点列表 —— 一个改动两处后果。
   * 没列到的分类按包顺序排在后面。
   */
  categoryOrder?: readonly string[]
}

/**
 * ★★ 资产的**每一条内部边**都要过一遍编辑器自己的类型规则。
 *
 * ⚠️ 这条是踩出来的，而且是最难查的一类：
 *
 * 一份资产里把 `time.part` 的输出（`number`）**直接接**到 `text.pad`
 * 的输入（`text`）。编辑器**刻意不允许**隐式转换（见 `types/socket.ts`
 * 的 `connectPlacement`：需要转换就插函数节点）——
 * 于是进这个资产的子图时，图引擎按 `isValidConnection` 复检，
 * 把那两条边**丢掉了**，控制台只剩一句 `An edge needs a source and a target`。
 *
 * 更坏的是它**求值是对的**（expr 不看类型，`helpers.pad` 拿到数字也能补零），
 * 所以「算出来没问题」完全掩盖了「图里少了两条线」。
 *
 * 校验期查出来只要一行字：「第 5 条边接不上，插个转换节点」。
 *
 * ⚠️ 用的是**编辑器那一份** `canConnect` + `findSocketIn`，不是另写一套 ——
 * 校验说的类型和编辑器认定的类型必须是同一个答案。
 */
function assetEdgeProblems(asset: GroupAssetDef): string[] {
  const problems: string[] = []
  asset.graph.edges.forEach((edge, index) => {
    const from = findSocketIn(asset.graph.nodes, edge.source, edge.sourceHandle)
    const to = findSocketIn(asset.graph.nodes, edge.target, edge.targetHandle)
    const where = `内部图第 ${index + 1} 条边（${edge.source}.${edge.sourceHandle ?? '?'} → ${edge.target}.${edge.targetHandle ?? '?'}）`
    if (!from || !to) {
      problems.push(`${where}：端口不存在（${from ? '目标' : '源'}端口没找到）`)
      return
    }
    if (!canConnect(from, to)) {
      problems.push(
        `${where}：类型接不上（${from.type} → ${to.type}）—— 中间要插一个转换节点`,
      )
    }
  })
  return problems
}

/** 内核那张分类图标表里有没有这个 id（没有就什么都不补） */
function iconOfCategory(id: string): { icon: string } | null {
  const icon = CATEGORY_ICONS[id]
  return icon ? { icon } : null
}

export function createNodeRegistry(
  packages: readonly NodePackage[],
  options: RegistryOptions = {},
): NodeRegistry {
  const nodeTypes: NodeTypeDef[] = []
  const categories: NodeCategoryDef[] = []
  const seenTypes = new Map<string, string>()
  const seenCategories = new Set<string>()

  for (const pack of packages) {
    for (const category of pack.categories) {
      if (seenCategories.has(category.id)) continue
      seenCategories.add(category.id)
      /*
       * ★ **图标回退**：包没声明就查内核那张表（`CATEGORY_ICONS`）。
       *
       * ⚠️ 在这里补而不是让每个包各写一份：`logic` / `text` / `number`
       * 这些是**基础能力**，而函数包 / 数据包是 **JSON 数据包** ——
       * 让它们各自抄一遍图标路径，就是同一批图标十份拷贝（改一处漏九处）。
       * 包自己声明了 `icon` 的照旧优先（域包想个性化完全可以）。
       */
      categories.push(
        category.icon ? category : { ...category, ...(iconOfCategory(category.id) ?? {}) },
      )
    }
    for (const def of pack.nodeTypes) {
      const owner = seenTypes.get(def.type)
      if (owner) {
        throw new Error(
          `节点类型标识重复：「${def.type}」同时由包「${owner}」和「${pack.id}」注册。` +
            '两个包不能定义同一个节点类型。',
        )
      }
      seenTypes.set(def.type, pack.id)
      nodeTypes.push(def)
    }
  }

  /* 按 `categoryOrder` 重排分类；没列到的保持原有相对顺序排后面 */
  const order = options.categoryOrder
  if (order) {
    const rank = new Map(order.map((id, i) => [id, i]))
    categories.sort((a, b) => {
      const ra = rank.get(a.id) ?? Number.MAX_SAFE_INTEGER
      const rb = rank.get(b.id) ?? Number.MAX_SAFE_INTEGER
      return ra === rb ? 0 : ra - rb
    })
  }

  const byType = new Map(nodeTypes.map((def) => [def.type, def]))
  const labelOf = new Map(categories.map((c) => [c.id, c.label]))

  /* ⚠️ 求值器同样**不许重复**：两个包抢同一个类型标识的实现，
   * 表现是「加载顺序决定谁生效」—— 和节点类型重复一样没有现场。 */
  const evaluators: Record<string, NodeEvaluator> = {}
  const runners: Record<string, NodeRunner> = {}
  const evaluatorOwner = new Map<string, string>()
  const codeProblems: NodeRegistry['codeProblems'] = []

  const claim = (type: string, packId: string, source: string) => {
    const owner = evaluatorOwner.get(type)
    if (owner) {
      throw new Error(
        `求值器重复：类型「${type}」同时由包「${owner}」和「${packId}」提供` +
          `（${source}）。一个节点类型只能有一个求值器。`,
      )
    }
    evaluatorOwner.set(type, packId)
  }

  for (const pack of packages) {
    /* ① 包级的函数求值器（代码包） */
    for (const [type, evaluator] of Object.entries(pack.evaluators ?? {})) {
      claim(type, pack.id, '来自 evaluators 表')
      evaluators[type] = evaluator
      /* 副作用执行器与求值器同源（同一个包），合并到一起 */
      const runner = pack.runners?.[type]
      if (runner) runners[type] = runner
    }
    /* ② 节点自带的源码求值器（数据包） */
    for (const def of pack.nodeTypes) {
      if (!def.code) continue
      claim(def.type, pack.id, '来自节点定义里的 code')
      const compiled = compileEvaluatorSource(def.code)
      if (compiled.ok) {
        evaluators[def.type] = compiled.evaluator
      } else {
        codeProblems.push({ type: def.type, problems: compiled.problems })
        /* 注册一个「一调用就抛」的占位：求值时那条诊断才说得清问题 */
        evaluators[def.type] = brokenEvaluator(def.type, compiled.problems)
      }
    }
  }

  /* ⚠️ 资产 id 也不许重复：两份同名资产在菜单里长得一模一样，
   * 点了哪一个全看顺序 —— 那种 bug 没法查 */
  const assets: GroupAssetDef[] = []
  const assetOwner = new Map<string, string>()
  for (const pack of packages) {
    for (const asset of pack.assets ?? []) {
      const owner = assetOwner.get(asset.id)
      if (owner) {
        throw new Error(
          `资产 id 重复：「${asset.id}」同时由包「${owner}」和「${pack.id}」提供。`,
        )
      }
      assetOwner.set(asset.id, pack.id)
      assets.push(asset)
    }
  }

  /*
   * ⚠️ 示例 id 同样不许重复（两份额外的示例在菜单里长得一模一样，
   * 点了哪一个全看加载顺序 —— 那种 bug 没法查）。查重口径和资产一致。
   */
  const examples: PackageExample[] = []
  const exampleOwner = new Map<string, string>()
  for (const pack of packages) {
    for (const example of pack.examples ?? []) {
      const owner = exampleOwner.get(example.id)
      if (owner) {
        throw new Error(
          `示例文档 id 重复：「${example.id}」同时由包「${owner}」和「${pack.id}」提供。`,
        )
      }
      exampleOwner.set(example.id, pack.id)
      examples.push(example)
    }
  }

  /*
   * 资产的两类问题一起查：
   *   ① 结构（缺接口节点 / 端口名数量对不上）—— `assetProblems()`
   *   ② 内部图引用了**目录里没有**的节点类型
   */
  const assetProblems: NodeRegistry['assetProblems'] = []
  for (const asset of assets) {
    const problems = assetProblemsOf(asset)
    for (const node of asset.graph.nodes) {
      /* 两个接口节点是组机制自带的，不在目录里 */
      if (node.id === GROUP_INPUT_NODE || node.id === GROUP_OUTPUT_NODE) continue
      if (!byType.has(node.type)) {
        problems.push(`内部图里的「${node.id}」用了目录里没有的节点类型：${node.type}`)
      }
    }
    problems.push(...assetEdgeProblems(asset))
    if (problems.length > 0) assetProblems.push({ asset: asset.id, problems })
  }

  return {
    nodeTypes,
    categories,
    evaluators,
    runners,
    codeProblems,
    assets,
    assetProblems,
    examples,
    nodeTypeOf: (type) => byType.get(type),
    packageOf: (type) => seenTypes.get(type),
    categoryLabel: (id) => labelOf.get(id) ?? id,
    makeModelOfType: (type, id) => {
      const def = byType.get(type)
      if (!def) return null
      return {
        id,
        type: def.type,
        title: def.title,
        category: def.category,
        width: def.width,
        height: def.height,
        render: def.render,
        editorOnly: def.editorOnly,
        /* ⚠️ 深拷贝：模板是共享的，实例必须是自己的 */
        rows: cloneRows(def.rows),
      }
    },
  }
}
